/**
 * LO STATO DI UNA WBS — derivato, perche' sul server non esiste.
 *
 * Una WBS e' l'albero dei `project-tasks` di una commessa: non ha una riga sua, quindi non ha
 * uno stato suo. Quello che si puo' dire di lei lo dicono due attributi della **commessa**:
 *
 *  · `wbsApprovedAt` — l'ultima approvazione (`POST /projects/{id}/wbs/approve`). Nullo vuol
 *    dire «mai approvata, nessun task generato»: e' la definizione di **bozza**;
 *  · `phase` — la fase della commessa. Una commessa completata, annullata o archiviata ha una
 *    WBS **chiusa**, approvata o no: non ci si lavora piu'.
 *
 * Il resto — approvata, su una commessa aperta — e' **in corso**.
 *
 * Lo leggono l'«Andamento» dei task (il badge) e la vista tabella dei progetti (il filtro).
 *
 * ⚠️ Lo stesso predicato vale in due posti: qui, per il badge di una riga gia' letta, e in
 * `wbsStateFilter`, per chiedere al server solo le righe di uno stato. Devono dire la stessa
 * cosa, o il filtro «Bozza» porterebbe righe col badge «Chiusa» — per questo il filtro Bozza
 * restringe **anche** alla fase aperta, e non solo a `wbsApprovedAt` nullo. La prova in
 * `tests/unit/wbsState.test.ts` confronta i due predicati su tutta la matrice.
 */
import type { FilterClause } from '@/shared/api/jsonapi/types';
import { OPEN_PHASES } from '@/features/projects/phase';

export type WbsState = 'draft' | 'active' | 'closed';

/**
 * `closed` non e' nel vocabolario del BE ma sta su righe vecchie: 114 sul dev il 09-10-2026.
 * Senza, quelle commesse risultavano «Bozza» e sparivano da tutti i filtri.
 */
export const LEGACY_CLOSED_PHASES = ['closed'] as const;

/** Le fasi in cui la commessa non si lavora piu' — il complemento di `OPEN_PHASES`. */
export const CLOSED_PHASES = ['completed', 'cancelled', 'archived', ...LEGACY_CLOSED_PHASES] as const;

export const WBS_STATE_OPTIONS = [
  { value: 'all', label: 'Tutte' },
  { value: 'draft', label: 'Bozza' },
  { value: 'active', label: 'In corso' },
  { value: 'closed', label: 'Chiusa' },
] as const satisfies readonly { value: WbsState | 'all'; label: string }[];

export const WBS_STATE_LABEL: Record<WbsState, string> = {
  draft: 'Bozza',
  active: 'In corso',
  closed: 'Chiusa',
};

/** Il colore del badge. Grigio la bozza, blu il lavoro, scuro il chiuso: come `statusColors`. */
export const WBS_STATE_COLOR: Record<WbsState, string> = {
  draft: 'gray',
  active: 'blue',
  closed: 'dark',
};

export interface WbsStateSource {
  phase?: string | null;
  wbsApprovedAt?: string | null;
}

export function deriveWbsState(row: WbsStateSource): WbsState {
  if (row.phase && (CLOSED_PHASES as readonly string[]).includes(row.phase)) return 'closed';
  if (!row.wbsApprovedAt) return 'draft';
  return 'active';
}

/**
 * Le clausole JSON:API che selezionano le commesse di uno stato.
 *
 * `isNull`/`isNotNull` diventano `equals(wbsApprovedAt,null)` e la sua negazione (`builder.ts`):
 * `null` non si cita, e `{ eq: null }` verrebbe scartato come «valore assente».
 */
export function wbsStateFilter(state: WbsState | 'all'): Record<string, FilterClause> {
  switch (state) {
    case 'draft':
      return { wbsApprovedAt: { isNull: true }, phase: { in: [...OPEN_PHASES] } };
    case 'active':
      return { wbsApprovedAt: { isNotNull: true }, phase: { in: [...OPEN_PHASES] } };
    case 'closed':
      return { phase: { in: [...CLOSED_PHASES] } };
    default:
      return {};
  }
}

/** Il filtro «WBS» della tabella progetti: sempre almeno un nodo, poi lo stato. */
export function wbsListFilter(state: WbsState | 'any'): Record<string, FilterClause> {
  return { wbsNodes: { has: true }, ...(state === 'any' ? {} : wbsStateFilter(state)) };
}
